/**
 * O overview do projeto, portado de app.js:469-491.
 *
 * Mesma ordem de leitura do painel atual: identidade e commit, os quatro números
 * do acervo, o cartão de adoção pelo agente, as notas do time e só então a prosa
 * gerada — que é rascunho e por isso vem por último, com a procedência no pé.
 */
import { useOverview, useProjetos } from '../api/consultas';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { CorpoComNotas } from '../notas/Notas';
import { CartaoDoAgente } from './CartaoDoAgente';

export function Overview({ slug }: { slug: string }) {
  const overview = useOverview(slug);
  const projetos = useProjetos();

  if (overview.isPending) return <Carregando rotulo={`o projeto ${slug}`} />;
  if (overview.error) {
    return <Falha erro={overview.error} rotulo={`o projeto ${slug}`} aoRepetir={overview.refetch} />;
  }

  const ov = overview.data!;
  const p = projetos.data?.find((x) => x.slug === slug);
  const ing = p?.lastIngest || {};
  const unidade = ov.unit_key || '__overview__';

  return (
    <>
      <div className="crumb"><b>{slug}</b><span className="sep">/</span>overview</div>

      <div className="hero-head">
        <div className="big-ico">
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
          </svg>
        </div>
        <div>
          <h1>{slug}</h1>
          <div className="sub">
            documentação viva · commit{' '}
            <code style={{ fontFamily: 'var(--mono)' }}>{(ing.sha_after || '').slice(0, 8)}</code>
          </div>
        </div>
      </div>

      <div className="stats">
        <div className="stat" title="documentos no acervo: overview, guia do agente e um por arquivo">
          <div className="n">{p?.docs || 0}</div><div className="l">documentos</div>
        </div>
        <div className="stat" title="nós do grafo: arquivos, classes, rotas, serviços">
          <div className="n">{p?.nodes || 0}</div><div className="l">nós</div>
        </div>
        <div className="stat" title="relações entre os nós: importa, expõe, injeta, implementa">
          <div className="n">{p?.edges || 0}</div><div className="l">relações</div>
        </div>
        <div className="stat" title="como o acervo foi montado da última vez: full reprocessa tudo, delta só o que mudou">
          <div className="n">{ing.mode || '—'}</div><div className="l">último ingest</div>
        </div>
      </div>

      <CartaoDoAgente slug={slug} />

      <CorpoComNotas
        slug={slug}
        unidade={unidade}
        docSha={ov.source_sha}
        corpo={ov.body_md}
        rotuloDaFaixa="valem para o projeto inteiro"
        vazioDoCorpo={(
          <Vazio titulo="Este projeto ainda não tem overview" comando={`eng reprocess ${slug}`}>
            O overview nasce quando o acervo é montado pela primeira vez. Se o projeto acabou de ser
            conectado, o job pode estar na fila; se já rodou, provavelmente falhou.
          </Vazio>
        )}
      />

      <div className="prov">
        <div className="k"><b>commit</b><span>{(ing.sha_after || '').slice(0, 8) || '—'}</span></div>
        <div className="k"><b>autor</b><span>{ing.author || '—'}</span></div>
        <div className="k"><b>data</b><span>{(ing.committed_at || '').slice(0, 10) || '—'}</span></div>
        <div className="k"><b>gerado por</b><span>{ov.generated_by || '—'}</span></div>
      </div>
    </>
  );
}
